import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { ClipboardCheck } from 'lucide-react';
import type { PerformanceMine, PerformanceReview, PerformanceSelfAssessment } from '@mnc/contracts';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { describeError } from '../../lib/errors';
import { pick } from '../../i18n';
import { Badge, EmptyState, Field, PageHeader, SearchableSelect, TableSkeleton, cn } from '../../components/ui';
import { Points, RadarChart, ReviewBadge, ScoreButtons, fmtDate, fmtPeriod, levelText } from './shared';

export const parseMyPerformanceSearch = (raw: Record<string, unknown>): { review?: string } => ({ review: typeof raw.review === 'string' && /^[0-9a-f-]{36}$/.test(raw.review) ? raw.review : undefined });

/** My performance: my shared reviews with the radar chart (MRLCO's "My performance"), my self-assessments, my acknowledgement. */
export function MyPerformancePage() {
  const { t, i18n } = useTranslation();
  const { can } = useAuth();
  const navigate = useNavigate();
  const search = parseMyPerformanceSearch(useSearch({ strict: false }) as Record<string, unknown>);
  const q = useQuery({ queryKey: ['performance', 'me'], queryFn: () => api.get<PerformanceMine>('/api/performance/me') });
  const [compare, setCompare] = useState(false);
  const lang = i18n.language;
  const reviews = q.data?.reviews ?? [];
  const index = Math.max(0, reviews.findIndex((r) => r.id === search.review));
  const review = reviews[index];
  const previous = compare ? reviews[index + 1] : undefined;
  const actions = can('performance_reviews') ? <button type="button" className="btn-secondary" onClick={() => void navigate({ to: '/performance/reviews', search: {} })}><ClipboardCheck size={16} /> {t('performance.reviewsPage')}</button> : undefined;

  return (
    <div>
      <PageHeader title={t('performance.pageTitle')} subtitle={t('performance.subtitle')} actions={actions} />
      {q.isLoading && <TableSkeleton />}
      {q.data && !q.data.is_employee && <EmptyState>{t('performance.notEmployee')}</EmptyState>}
      <div className="grid grid-cols-1 content-start gap-4">
        {q.data?.self_assessments.map((s) => <SelfAssessmentCard key={s.cycle.id} s={s} />)}
        {q.data?.is_employee && reviews.length === 0 && <EmptyState>{t('performance.noReviews')}</EmptyState>}
        {review && (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <div className="w-72 max-w-full"><SearchableSelect value={review.id} onChange={(v) => void navigate({ to: '/performance', search: { review: v as string } })} options={reviews.map((r) => ({ value: r.id, label: pick(lang, r.cycle.name_en, r.cycle.name_ar), description: fmtPeriod(r.cycle.period_start, r.cycle.period_end, lang) }))} /></div>
              {reviews[index + 1] && <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={compare} onChange={(e) => setCompare(e.target.checked)} />{t('performance.compare', { cycle: pick(lang, reviews[index + 1]!.cycle.name_en, reviews[index + 1]!.cycle.name_ar) })}</label>}
            </div>
            <div className="grid gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
              <div className="card p-4">
                <RadarChart criteria={review.criteria} scores={review.scores.map((s) => s.score)} previous={previous ? review.criteria.map((c) => previous.scores.find((_s, i) => previous.criteria[i]?.code === c.code)?.score ?? null) : null} size={420} />
                {previous && <p className="mt-2 text-center text-xs text-neutral-500"><span className="me-3 inline-block h-0.5 w-6 bg-brand-600 align-middle" />{pick(lang, review.cycle.name_en, review.cycle.name_ar)}<span className="ms-4 me-3 inline-block w-6 border-t-2 border-dashed border-neutral-400 align-middle" />{pick(lang, previous.cycle.name_en, previous.cycle.name_ar)}</p>}
              </div>
              <div className="card grid content-start gap-3 p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <div className="text-2xl font-semibold"><Points points={review.total_points} percent={review.percent} /></div>
                    {previous && <Delta value={review.total_points - previous.total_points} />}
                  </div>
                  <ReviewBadge status={review.status} />
                </div>
                <p className="text-xs text-neutral-500">{t('performance.byReviewer', { name: review.reviewer ? pick(lang, review.reviewer.display_name_en, review.reviewer.display_name_ar) : '—', date: fmtDate(review.submitted_at, lang) })}</p>
                <ul className="divide-y divide-neutral-100 dark:divide-neutral-800">
                  {review.criteria.map((c, i) => {
                    const s = review.scores[i]?.score ?? null;
                    const before = previous?.scores.find((_x, k) => previous.criteria[k]?.code === c.code)?.score ?? null;
                    return (
                      <li key={c.id} className="py-2">
                        <div className="flex items-center justify-between gap-2 text-sm">
                          <span className="min-w-0 font-medium" dir="auto">{pick(lang, c.name_en, c.name_ar)} <span className="text-xs font-normal text-neutral-500">{c.weight}%</span></span>
                          <span className="flex items-center gap-2 tabular-nums"><span className="font-semibold">{s ?? '—'}</span>{previous && before !== null && s !== null && <Delta value={s - before} small />}</span>
                        </div>
                        <div className="mt-1 h-1.5 rounded-full bg-neutral-100 dark:bg-neutral-800"><div className="h-1.5 rounded-full bg-brand-500" style={{ width: `${((s ?? 0) / 5) * 100}%` }} /></div>
                        <p className="mt-1 text-xs text-neutral-500" dir="auto">{levelText(c, s, lang)}</p>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
            <ReviewTexts review={review} />
            {review.can_acknowledge && <AcknowledgeCard review={review} />}
          </>
        )}
      </div>
    </div>
  );
}

function Delta({ value, small }: { value: number; small?: boolean }) {
  if (value === 0) return <span className={cn('text-neutral-500', small ? 'text-xs' : 'text-sm')}>±0</span>;
  return <span className={cn(value > 0 ? 'text-brand-700 dark:text-brand-300' : 'text-red-600', small ? 'text-xs' : 'text-sm')} dir="ltr">{value > 0 ? '+' : ''}{value}</span>;
}

function ReviewTexts({ review }: { review: PerformanceReview }) {
  const { t, i18n } = useTranslation();
  const block = (label: string, text: string | null) => text && (
    <div>
      <h3 className="text-xs font-semibold uppercase tracking-wide text-neutral-500">{label}</h3>
      <p className="mt-1 whitespace-pre-wrap text-sm" dir="auto">{text}</p>
    </div>
  );
  if (!review.summary && !review.objectives && !review.self_comment && !review.employee_comment) return null;
  return (
    <div className="card grid gap-4 p-4 md:grid-cols-2">
      {block(t('performance.summary'), review.summary)}
      {block(t('performance.objectives'), review.objectives)}
      {block(t('performance.selfComment'), review.self_comment)}
      {review.acknowledged_at && block(t('performance.acknowledgedOn', { date: fmtDate(review.acknowledged_at, i18n.language) }), review.employee_comment || t('performance.noComment'))}
    </div>
  );
}

function AcknowledgeCard({ review }: { review: PerformanceReview }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [comment, setComment] = useState('');
  const ack = useMutation({
    mutationFn: () => api.post(`/api/performance/reviews/${review.id}/acknowledge`, { comment: comment.trim() || null }),
    onSuccess: () => { toast.success(t('performance.acknowledged')); void qc.invalidateQueries({ queryKey: ['performance'] }); },
    onError: (e) => toast.error(describeError(e, t)),
  });
  return (
    <div className="card grid gap-3 border-amber-300 p-4 dark:border-amber-700">
      <h2 className="font-semibold">{t('performance.acknowledgeTitle')}</h2>
      <p className="text-sm text-neutral-500">{t('performance.acknowledgeHint')}</p>
      <Field label={t('performance.yourComment')}><textarea className="input" rows={3} dir="auto" value={comment} onChange={(e) => setComment(e.target.value)} /></Field>
      <div><button type="button" className="btn-primary" disabled={ack.isPending} onClick={() => ack.mutate()}>{t('performance.acknowledge')}</button></div>
    </div>
  );
}

/** The person scores themselves first; saved as they go, submitted once, then it is the reviewer's to read. */
function SelfAssessmentCard({ s }: { s: PerformanceSelfAssessment }) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const lang = i18n.language;
  const [scores, setScores] = useState<Record<string, number | null>>({});
  const [comment, setComment] = useState('');
  useEffect(() => { setScores(Object.fromEntries(s.scores.map((x) => [x.criterion_id, x.self_score]))); setComment(s.comment ?? ''); }, [s]);
  const submitted = Boolean(s.submitted_at);
  const body = () => ({ scores: Object.entries(scores).map(([criterion_id, score]) => ({ criterion_id, score })), comment: comment.trim() || null });
  const save = useMutation({ mutationFn: () => api.put(`/api/performance/me/cycles/${s.cycle.id}/self`, body()), onSuccess: () => { toast.success(t('performance.saved')); void qc.invalidateQueries({ queryKey: ['performance'] }); }, onError: (e) => toast.error(describeError(e, t)) });
  const submit = useMutation({
    mutationFn: async () => { await api.put(`/api/performance/me/cycles/${s.cycle.id}/self`, body()); return api.post(`/api/performance/me/cycles/${s.cycle.id}/self/submit`); },
    onSuccess: () => { toast.success(t('performance.selfSubmitted')); void qc.invalidateQueries({ queryKey: ['performance'] }); },
    onError: (e) => toast.error(describeError(e, t)),
  });
  const complete = s.criteria.every((c) => scores[c.id] !== null && scores[c.id] !== undefined);
  return (
    <div className="card grid gap-3 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-semibold">{t('performance.selfTitle', { cycle: pick(lang, s.cycle.name_en, s.cycle.name_ar) })}</h2>
        {submitted ? <Badge tone="brand">{t('performance.selfDone', { date: fmtDate(s.submitted_at, lang) })}</Badge> : <Badge tone="warn">{t('performance.selfOpen')}</Badge>}
      </div>
      <p className="text-sm text-neutral-500">{t('performance.selfHint')}</p>
      <div className="grid gap-3 md:grid-cols-2">
        {s.criteria.map((c) => (
          <div key={c.id} className="rounded-lg border border-neutral-200 p-3 dark:border-neutral-700">
            <div className="mb-2 text-sm font-medium" dir="auto">{pick(lang, c.name_en, c.name_ar)} <span className="text-xs font-normal text-neutral-500">{c.weight}%</span></div>
            <ScoreButtons criterion={c} value={scores[c.id] ?? null} onChange={submitted ? undefined : (v) => setScores({ ...scores, [c.id]: v })} disabled={submitted} />
          </div>
        ))}
      </div>
      <Field label={t('performance.selfComment')}><textarea className="input" rows={3} dir="auto" value={comment} disabled={submitted} onChange={(e) => setComment(e.target.value)} /></Field>
      {!submitted && (
        <div className="flex flex-wrap gap-2">
          <button type="button" className="btn-secondary" disabled={save.isPending} onClick={() => save.mutate()}>{t('app.save')}</button>
          <button type="button" className="btn-primary" disabled={submit.isPending || !complete} title={complete ? undefined : t('performance.scoreEvery')} onClick={() => submit.mutate()}>{t('performance.submitSelf')}</button>
        </div>
      )}
    </div>
  );
}
